import type { ReactNode } from "react"

import { Heading } from "@/components/heading"
import { SkillBadge } from "@/components/skill-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { formatGold, formatTargetNumber } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { PositionDef } from "@/types"

interface PositionalShopProps {
  positions: PositionDef[]
  /** Current hired count for a position (draft: reducer state, manage: roster_counts). */
  count: (position: PositionDef) => number
  /** Whether the Hire button is disabled (roster cap, budget, quantity, etc.). */
  hireDisabled: (position: PositionDef) => boolean
  onHire: (position: PositionDef) => void
  /**
   * Optional content rendered directly below a position row (e.g. manage's inline
   * hire form). Returning a non-null node moves the row's bottom border onto the
   * expanded content so the group reads as one bordered unit.
   */
  renderExpanded?: (position: PositionDef, isLast: boolean) => ReactNode
}

export function PositionalShop({
  positions,
  count,
  hireDisabled,
  onHire,
  renderExpanded,
}: PositionalShopProps) {
  return (
    <Card className="py-0">
      <CardContent className="pt-5 pb-4">
        <Heading level={2} wrapperClassName="mb-4">
          Available Positionals
        </Heading>

        {/* Stat header row */}
        <div
          className="flex items-center gap-2 border-b px-3 pb-2"
          style={{ borderColor: "var(--border)" }}
        >
          <div
            className="flex-1 text-xs font-bold tracking-widest uppercase"
            style={{ color: "var(--accent-dim)" }}
          >
            Position
          </div>
          {(["MA", "ST", "AG", "PA", "AV"] as const).map((s) => (
            <div
              key={s}
              className="font-display text-accent-dim w-8 shrink-0 text-center text-[10px] font-bold tracking-[0.05em] uppercase"
            >
              {s}
            </div>
          ))}
          <div
            className="w-16 text-center text-[10px] font-bold tracking-widest uppercase"
            style={{ color: "var(--accent-dim)" }}
          >
            Cost
          </div>
          <div
            className="w-12 text-center text-[10px] font-bold tracking-widest uppercase"
            style={{ color: "var(--accent-dim)" }}
          >
            Qty
          </div>
          <div className="w-16" />
        </div>

        {/* Position rows */}
        {positions.map((position, index) => {
          const positionCount = count(position)
          const atMax = positionCount >= position.quantity
          const isLast = index === positions.length - 1
          const expanded = renderExpanded?.(position, isLast)
          const hasExpanded = expanded != null && expanded !== false

          return (
            <div key={position.name}>
              <div
                className={cn(
                  "flex items-center gap-2 px-3 py-2.5 transition-[background] duration-100 hover:bg-[oklch(0.1_0.005_230)]",
                  !isLast && !hasExpanded && "border-b",
                )}
                style={{ borderColor: "var(--border)" }}
              >
                {/* Position name + skill badges */}
                <div className="flex-1">
                  <span className="text-sm font-semibold">{position.name}</span>
                  {position.starting_skills.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {position.starting_skills.map((skill) => (
                        <SkillBadge key={skill} name={skill} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Stat cells */}
                {(["ma", "st", "ag", "pa", "av"] as const).map((stat) => (
                  <div
                    key={stat}
                    className="font-display w-8 shrink-0 text-center text-xs font-semibold"
                  >
                    {formatTargetNumber(position[stat], stat.toUpperCase())}
                  </div>
                ))}

                {/* Cost */}
                <div className="w-16 text-center text-xs font-semibold">
                  {formatGold(position.cost)}
                </div>

                {/* Count ring */}
                <div className="flex w-12 justify-center">
                  <div
                    className={cn(
                      "border-border-heavy font-display flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold",
                      atMax &&
                        "border-[oklch(0.4_0.1_28)] text-[oklch(0.65_0.15_28)]",
                    )}
                  >
                    {positionCount}
                  </div>
                </div>

                {/* Hire button */}
                <div className="flex w-16 justify-center">
                  <Button
                    type="button"
                    variant="default"
                    className="px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase"
                    disabled={hireDisabled(position)}
                    onClick={() => onHire(position)}
                    aria-label={`Hire ${position.name}`}
                  >
                    Hire
                  </Button>
                </div>
              </div>

              {expanded}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
